import { create } from 'zustand';
import { apiFetch } from '../lib/api';

/**
 * [ADR-069 §3/§13.3 / D10 #915] Cache do `acesso` (ResumoAcesso) devolvido por
 * `GET /auth/me` — a mesma fonte que o backend já usa para decidir RBAC
 * (`ContextoAcessoService.resumirAcesso`), agora também consumida pelo frontend.
 *
 * Por que store separado de `useAuthStore`/`useEstabelecimentosStore`: `acesso`
 * é dado de RBAC (pode mudar sem novo login — concessão/revogação em tempo real,
 * ADR-069 §4 regra 5), diferente do payload de login (`useAuthStore`) e do cache
 * de config de estabelecimentos (`useEstabelecimentosStore`, que é a fonte LEGADA
 * — `GET /estabelecimentos`, só-ADMIN, mantida para usuários ainda sem nenhuma
 * `UsuarioAcesso` — ver `SeletorEstabelecimento`).
 *
 * `atribuicoes.length === 0` é o sinal de "usuário sem nenhuma atribuição RBAC" —
 * `resumirAcesso` documenta isso: "Um contexto vazio vira listas vazias." [ADR-069 §13.27 /
 * #948] Até esta entrega, consumidores tratavam esse caso como "RBAC ainda não se aplica a
 * este usuário" (compatibilidade pré-backfill, fail-open). A rede de segurança do deploy
 * (backfill rodado ANTES do código novo subir) torna esse estado impossível em QA/PROD pra
 * usuário normal — `usePermissao().pode()` agora nega por padrão nesse caso (fail-closed,
 * §13.27). O uso de `temAtribuicaoRbac` que SOBROU depois dessa entrega (`SeletorEstabelecimento`,
 * `use-estabelecimentos-visiveis.ts`, `EstabelecimentosBoot` no App.tsx, `rotuloPerfil` no
 * AppShell) é só SELEÇÃO DE FONTE DE DADOS (RBAC novo vs. `/estabelecimentos` legado,
 * só-ADMIN) — não é um bypass de permissão, então não precisou mudar: na pior hipótese
 * (revogação total transitória) ele cai pro endpoint legado, que o próprio `@Roles` do
 * backend já protege.
 */

export interface EstabelecimentoPermitido {
  id: number;
  nome: string;
}

/** Espelha `PorChave<T>` do backend (`contexto-acesso.service.ts`). */
export interface PorChave<T> {
  tenant_wide: T;
  por_estabelecimento: Record<string, T>;
}

export interface AcessoResumo {
  versao: number;
  plataforma: boolean;
  tenant_wide: boolean;
  atribuicoes: Array<{
    id: number;
    perfil: { id: number; nome: string; tipo: string; de_fabrica: boolean };
    estabelecimento: { id: number; nome: string } | null;
  }>;
  estabelecimentos_permitidos: EstabelecimentoPermitido[];
  /** [F4.3 #890] Códigos de permissão por chave — usado por `usePermissao()`. */
  permissoes: PorChave<string[]>;
}

const TTL_MS = 60_000;

interface AcessoState {
  acesso: AcessoResumo | null;
  carregado: boolean;
  ultimo_fetch: number | null;
  carregando: boolean;
  /** Busca /auth/me e cacheia `acesso`. `forcar=true` ignora o TTL de 60s. */
  carregar: (opts?: { forcar?: boolean }) => Promise<void>;
  limpar: () => void;
}

export const useAcessoStore = create<AcessoState>((set, get) => ({
  acesso: null,
  carregado: false,
  ultimo_fetch: null,
  carregando: false,

  carregar: async ({ forcar } = {}) => {
    const { ultimo_fetch, carregando } = get();
    if (carregando) return;
    if (!forcar && ultimo_fetch !== null && Date.now() - ultimo_fetch < TTL_MS) return;
    set({ carregando: true });
    try {
      const res = await apiFetch('/auth/me');
      if (!res.ok) {
        set({ carregando: false, carregado: true });
        return;
      }
      const data = (await res.json()) as { acesso?: AcessoResumo | null };
      set({
        acesso: data.acesso ?? null,
        carregado: true,
        ultimo_fetch: Date.now(),
        carregando: false,
      });
    } catch {
      set({ carregando: false, carregado: true });
    }
  },

  limpar: () => set({ acesso: null, carregado: false, ultimo_fetch: null, carregando: false }),
}));

/** true quando `/auth/me` já respondeu E o usuário tem pelo menos uma UsuarioAcesso (RBAC ativo). */
export function temAtribuicaoRbac(acesso: AcessoResumo | null): boolean {
  return acesso !== null && acesso.atribuicoes.length > 0;
}
